import { Injectable } from '@angular/core';
import * as THREE from 'three';
import { WorkbenchCollisionService } from './workbench-collision.service';
import { ShapeTypeEnum } from '../../../../../enum/workbench/shape-type.enum';
import { AngleTypeEnum } from '../../../../../enum/workbench/angle-type-enum';
import { ConfigurationPlan } from '../../../../../models/configurators-2d/applications/workbench/configurations/configuration-plan';
import { ActionButton, DimensionIndicator, MagnetismIcon, PlanIndicator, GroupIndicator } from '../preview-ui-models';
import { Point2D } from 'src/app/utils/math/collision-utils';
import { InteractionModeEnum } from 'src/app/stores/workbench/workbench-selection.store';
import {
  getWorkbenchSideSegment,
  inferWorkbenchSideFromSegment,
  WorkbenchSide
} from './workbench-side-geometry';
import { inferWorkbenchEdgeSide } from './workbench-edge-utils';

@Injectable({
  providedIn: 'root'
})
export class WorkbenchDimensionsService {

  constructor(private readonly collisionService: WorkbenchCollisionService) { }

  private getEdgeProbePoints(
    plan: ConfigurationPlan,
    obj: THREE.Object3D,
    edge: 'left' | 'right' | 'top' | 'bottom'
  ): THREE.Vector3[] {
    const segment = getWorkbenchSideSegment(plan, edge as WorkbenchSide);
    if (!segment) return [];

    const centerOffset = obj.userData['centerOffset'] || (
      plan.shape.shapeType === ShapeTypeEnum.Angle
        ? { x: plan.length / 2, y: plan.length / 2 }
        : { x: plan.length / 2, y: plan.width / 2 }
    );

    const dx = segment.end.x - segment.start.x;
    const dy = segment.end.y - segment.start.y;
    const segmentLength = Math.hypot(dx, dy);

    if (segmentLength < 1) return [];

    const cornerInset = Math.min(20, segmentLength / 4);
    const sampleTs = segmentLength > cornerInset * 2 + 1
      ? [cornerInset / segmentLength, 0.5, 1 - (cornerInset / segmentLength)]
      : [0.5];

    return sampleTs.map(t => new THREE.Vector3(
      segment.start.x + dx * t - centerOffset.x,
      segment.start.y + dy * t - centerOffset.y,
      0
    ));
  }

  /**
   * Main method to calculate all UI overlays (Dimensions, Indicators, Buttons, Magnetism).
   */
  public calculateOverlays(
    activePlans: THREE.Object3D[],
    camera: THREE.Camera,
    containerWidth: number,
    containerHeight: number,
    step: number,
    interactionMode: InteractionModeEnum,
    selectedObject: THREE.Object3D | null,
    is3DMode: boolean,
    selectedCuttingData?: { planGroupId: number; cuttingIdx: number } | null
  ): {
    actionButtons: ActionButton[],
    dimensions: DimensionIndicator[],
    planIndicators: PlanIndicator[],
    groupIndicators: GroupIndicator[],
    magnetismIcons: MagnetismIcon[],
    splashbackIndicators: any[]
  } {
    const actionButtons: ActionButton[] = [];
    const dimensions: DimensionIndicator[] = [];
    const planIndicators: PlanIndicator[] = [];
    const groupIndicators: GroupIndicator[] = [];
    const magnetismIcons: MagnetismIcon[] = [];
    const splashbackIndicators: any[] = [];
    const groups: { [key: string]: THREE.Object3D[] } = {};
    const groupBounds: { [key: string]: { minX: number, maxX: number, minY: number, maxY: number } } = {};
    const groupLabelMap: { [key: string]: string } = {};
    const planDimensionSides: { [planId: number]: { horizontal?: 'top' | 'bottom', vertical?: 'left' | 'right' } } = {};
    const groupDimensionSides: { [groupId: string]: { top?: boolean, bottom?: boolean, left?: boolean, right?: boolean } } = {};

    // Helpers
    const toScreenPosition = (objPos: THREE.Vector3) => {
      const vector = objPos.clone();
      vector.project(camera);
      const x = (vector.x * .5 + .5) * containerWidth;
      const y = (-(vector.y * .5) + .5) * containerHeight;
      return { x, y };
    };

    const getProjectedAngle = (p1: THREE.Vector3, p2: THREE.Vector3) => {
      const s1 = toScreenPosition(p1);
      const s2 = toScreenPosition(p2);
      let angle = Math.atan2(s2.y - s1.y, s2.x - s1.x) * (180 / Math.PI);

      // Normalize to [-90, 90] to ensure text is always upright
      if (angle > 90) angle -= 180;
      if (angle < -90) angle += 180;

      // Force explicitly to -90 for uniform vertical texts reading bottom-to-top
      if (Math.abs(angle - 90) < 0.1) angle = -90;

      return angle;
    };

    // ---------------------------------------------------------
    // 0. Pre-calculate Collisions & Bounds (Optimization)
    // ---------------------------------------------------------
    const planCollisionCache = new Map<number, { polygons: any[], bounds: any }>();

    // activePlans is now passed as argument
    // const activePlans = scene.children.filter(obj => obj.userData['plan'] && obj.visible);

    activePlans.forEach(obj => {
      const polygons = this.collisionService.getCollisionPolygons(obj);
      const bounds = this.collisionService.getBoundsFromPolygons(polygons);
      planCollisionCache.set(obj.userData['id'], { polygons, bounds });
    });

    const getCachedData = (objOrId: any) => {
      const id = typeof objOrId === 'number' ? objOrId : objOrId.userData['id'];
      return planCollisionCache.get(id);
    };

    const toWorldForObject = (object: THREE.Object3D, vector: THREE.Vector3) =>
      vector.clone().applyEuler(object.rotation).add(object.position);

    const distToSegmentSquared = (p: { x: number, y: number }, v: { x: number, y: number }, w: { x: number, y: number }) => {
      const l2 = (v.x - w.x) ** 2 + (v.y - w.y) ** 2;
      if (l2 === 0) return (p.x - v.x) ** 2 + (p.y - v.y) ** 2;
      let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
      t = Math.max(0, Math.min(1, t));
      const px = v.x + t * (w.x - v.x);
      const py = v.y + t * (w.y - v.y);
      return (p.x - px) ** 2 + (p.y - py) ** 2;
    };

    const pointTouchesOtherPlans = (point: THREE.Vector3, source: THREE.Object3D) => {
      const THRESHOLD_SQ = 5 * 5;

      return activePlans.some(other => {
        if (other === source) return false;

        const polygons = getCachedData(other)?.polygons || this.collisionService.getCollisionPolygons(other);
        if (!polygons || polygons.length === 0) return false;

        return polygons.some(poly => poly.some((vertex, index) => {
          const nextVertex = poly[(index + 1) % poly.length];
          return distToSegmentSquared({ x: point.x, y: point.y }, vertex, nextVertex) < THRESHOLD_SQ;
        }));
      });
    };

    // ---------------------------------------------------------
    // 1. Pre-calculate Groups
    // ---------------------------------------------------------

    // Use Service to find groups
    const groupsIds = this.collisionService.findGroups(activePlans);
    groupsIds.forEach((ids, index) => {
      // Only treat as a group if it has more than 1 item
      if (ids.length <= 1) return;

      const groupId = `group_${index}`;
      groups[groupId] = [];

      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

      ids.forEach(id => {
        const obj = activePlans.find(o => o.userData['id'] === id);
        if (obj) {
          obj.userData['groupId'] = groupId; // Update transient user data
          groups[groupId].push(obj);

          // Calculate Group Bounds for exterior dimension placement
          const polygons = getCachedData(id)?.polygons || this.collisionService.getCollisionPolygons(obj);
          polygons.forEach(poly => {
            poly.forEach(p => {
              if (p.x < minX) minX = p.x;
              if (p.x > maxX) maxX = p.x;
              if (p.y < minY) minY = p.y;
              if (p.y > maxY) maxY = p.y;
            });
          });
          groupBounds[groupId] = { minX, maxX, minY, maxY };
        }
      });
      groupLabelMap[groupId] = `G${index + 1}`;
    });

    // ---------------------------------------------------------
    // Pre-calculate Group Dimensions (To align individual modifiable dims)
    // ---------------------------------------------------------
    if (step >= 1) {
      Object.keys(groups).forEach((gid) => {
        const members = groups[gid];
        if (members.length === 0) return;

        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        let maxPlanWidth = 0;
        let maxPlanLength = 0;
        const planBounds: { id: number, minX: number, maxX: number, minY: number, maxY: number }[] = [];

        members.forEach(m => {
          const plan = m.userData['plan'];
          if (plan) {
            let rot = Math.round(m.rotation.z * (180 / Math.PI)) % 360;
            if (rot < 0) rot += 360;
            const isRot90 = Math.abs(rot - 90) < 1 || Math.abs(rot - 270) < 1;
            const visualWidth = isRot90 ? plan.width : plan.length;
            const visualHeight = isRot90 ? plan.length : plan.width;

            if (visualWidth > maxPlanWidth) maxPlanWidth = visualWidth;
            if (visualHeight > maxPlanLength) maxPlanLength = visualHeight;

            const polygons = getCachedData(m)?.polygons || this.collisionService.getCollisionPolygons(m);
            let pMinX = Infinity, pMaxX = -Infinity, pMinY = Infinity, pMaxY = -Infinity;

            let hasPoints = false;
            polygons.forEach((poly: Point2D[]) => {
              poly.forEach(p => {
                hasPoints = true;
                if (p.x < minX) minX = p.x;
                if (p.x > maxX) maxX = p.x;
                if (p.y < minY) minY = p.y;
                if (p.y > maxY) maxY = p.y;
                if (p.x < pMinX) pMinX = p.x;
                if (p.x > pMaxX) pMaxX = p.x;
                if (p.y < pMinY) pMinY = p.y;
                if (p.y > pMaxY) pMaxY = p.y;
              });
            });
            if (hasPoints) {
              planBounds.push({ id: plan.id, minX: pMinX, maxX: pMaxX, minY: pMinY, maxY: pMaxY });
            }
          }
        });

        if (Number.isFinite(minX)) {
          const checkSides = [
            { name: 'left', base: minX, axis: 'y', check: (b: any) => Math.abs(b.minX - minX) < 40 },
            { name: 'right', base: maxX, axis: 'y', check: (b: any) => Math.abs(b.maxX - maxX) < 40 },
            { name: 'top', base: maxY, axis: 'x', check: (b: any) => Math.abs(b.maxY - maxY) < 40 },
            { name: 'bottom', base: minY, axis: 'x', check: (b: any) => Math.abs(b.minY - minY) < 40 }
          ];

          const sideSpans: { [key: string]: { spanMin: number, spanMax: number, spanLength: number, axis: string } } = {};

          const getMergedSpans = (intervals: { min: number, max: number }[]) => {
            if (intervals.length === 0) return [];
            intervals.sort((a, b) => a.min - b.min);
            const merged = [intervals[0]];
            for (let i = 1; i < intervals.length; i++) {
              const prev = merged[merged.length - 1];
              const curr = intervals[i];
              if (curr.min <= prev.max + 10) {
                prev.max = Math.max(prev.max, curr.max);
              } else {
                merged.push(curr);
              }
            }
            return merged;
          };

          checkSides.forEach(side => {
            const aligned = planBounds.filter(b => side.check(b));
            if (aligned.length === 0) return;

            const axisIntervals = aligned.map(b => side.axis === 'y' ? { min: b.minY, max: b.maxY } : { min: b.minX, max: b.maxX });
            const merged = getMergedSpans(axisIntervals);

            if (merged.length === 1) {
              const spanMin = merged[0].min;
              const spanMax = merged[0].max;
              const spanLength = spanMax - spanMin;
              const maxSingle = side.axis === 'y' ? maxPlanLength : maxPlanWidth;

              if (spanLength > maxSingle + 10) {
                sideSpans[side.name] = { spanMin, spanMax, spanLength, axis: side.axis };
              }
            }
          });

          // Preliminary conflict resolution using standard preferences (right/top)
          if (sideSpans['top'] && sideSpans['bottom']) delete sideSpans['bottom'];
          if (sideSpans['left'] && sideSpans['right']) delete sideSpans['left'];

          groupDimensionSides[gid] = {};
          Object.keys(sideSpans).forEach(s => {
            groupDimensionSides[gid][s as 'top' | 'bottom' | 'left' | 'right'] = true;
          });
        }
      });
    }

    // ----------------------
    // Précalcul des côtés assemblés dans le repère local du plan.
    // Un côté est "assemblé" si ses points de sondage touchent réellement un autre plan
    // une fois transformés dans le monde, ce qui gère correctement rotations et plans d'angle.
    // ----------------------
    const assembledSides = new Map<number, Set<string>>();
    activePlans.forEach(obj => {
      const plan = obj.userData['plan'] as ConfigurationPlan;
      const sides = new Set<string>();

      (['top', 'bottom', 'left', 'right'] as WorkbenchSide[]).forEach(side => {
        const probePoints = this.getEdgeProbePoints(plan, obj, side);
        const isAssembled = probePoints
          .map(point => toWorldForObject(obj, point))
          .some(point => pointTouchesOtherPlans(point, obj));

        if (isAssembled) sides.add(side);
      });

      if (sides.size > 0) {
        assembledSides.set(plan.displayOrder, sides);
      }
    });

    // ----------------------
    // Finishes Buttons (Step 3+) / Action Buttons
    // Step 3 = Finitions (non-Quartz) or Crédence (Quartz)
    // Step 4 = Finitions (Quartz, crédence step pushed it to 4)
    // ----------------------
    if ((interactionMode !== InteractionModeEnum.NONE && step >= 3) || interactionMode === InteractionModeEnum.SNAPSHOT) {
      activePlans.forEach(obj => {
        // Filter checks (from component)
        if (!obj.userData['plan']) return;
        const plan = obj.userData['plan'] as ConfigurationPlan;

        const toWorld = (v: THREE.Vector3) => v.clone().applyEuler(obj.rotation).add(obj.position);

        if (interactionMode === InteractionModeEnum.EDGE || interactionMode === InteractionModeEnum.SNAPSHOT) {
          const addBtn = (idSuffix: number, localPoint: THREE.Vector3, side: string) => {
            const worldPos = toWorld(localPoint);
            const screenPos = toScreenPosition(worldPos);

            // For Quartz (canHaveSideCuts), polished edges are stored in configurationSideCuts
            const edgeCollection: any[] = plan.materialThickness?.canHaveSideCuts
              ? (plan.configurationSideCuts ?? [])
              : (plan.configurationEdges ?? []);

            const isActive = edgeCollection.some((e: any) => {
              return inferWorkbenchEdgeSide(plan, e, 5) === side;
            });

            actionButtons.push({
              id: `edge_${idSuffix}_${plan.displayOrder}`,
              type: 'finish_click',
              x: screenPos.x,
              y: screenPos.y,
              icon: 'edge_band',
              tooltip: 'Chant',
              targetId: plan.displayOrder,
              isActive: isActive,
              data: { mode: 'edge', side, localPoint: localPoint }
            });
          };

          const L = plan.length;
          const W = plan.width;
          const centerOffset = obj.userData['centerOffset'] || { x: L / 2, y: W / 2 };

          switch (plan.shape.shapeType) {
            case ShapeTypeEnum.Rectangle: {
              addBtn(1, new THREE.Vector3(L / 2 - centerOffset.x, W - centerOffset.y, 0), 'top');
              addBtn(2, new THREE.Vector3(L / 2 - centerOffset.x, 0 - centerOffset.y, 0), 'bottom');
              addBtn(3, new THREE.Vector3(0 - centerOffset.x, W / 2 - centerOffset.y, 0), 'left');
              addBtn(4, new THREE.Vector3(L - centerOffset.x, W / 2 - centerOffset.y, 0), 'right');
              break;
            }
            case ShapeTypeEnum.HalfMoon: {
              const rHM = W / 2;
              addBtn(1, new THREE.Vector3((L - rHM) / 2 - centerOffset.x, W - centerOffset.y, 0), 'top');
              addBtn(2, new THREE.Vector3((L - rHM) / 2 - centerOffset.x, 0 - centerOffset.y, 0), 'bottom');
              addBtn(3, new THREE.Vector3(0 - centerOffset.x, W / 2 - centerOffset.y, 0), 'left');
              addBtn(4, new THREE.Vector3(L - centerOffset.x, W / 2 - centerOffset.y, 0), 'arc');
              break;
            }
            case ShapeTypeEnum.Mushroom: {
              const rM = (plan.diameter || W) / 2;
              const isFlip = plan.isFlip;
              const adjX = Math.sqrt(Math.max(0, rM ** 2 - (W - rM) ** 2));
              addBtn(1, new THREE.Vector3((L - rM - adjX) / 2 - centerOffset.x, W - centerOffset.y, 0), 'top');
              addBtn(2, new THREE.Vector3((L - rM) / 2 - centerOffset.x, 0 - centerOffset.y, 0), 'bottom');
              addBtn(3, new THREE.Vector3(0 - centerOffset.x, W / 2 - centerOffset.y, 0), 'left');
              addBtn(4, new THREE.Vector3(L - centerOffset.x, (isFlip ? (W - rM) : rM) - centerOffset.y, 0), 'arc');
              break;
            }
            case ShapeTypeEnum.Angle: {
              addBtn(1, new THREE.Vector3(W / 2 - centerOffset.x, 0 - centerOffset.y, 0), 'bottom');
              addBtn(2, new THREE.Vector3((W + L) / 2 - centerOffset.x, (L - W) / 2 - centerOffset.y, 0), 'diagonal');
              addBtn(3, new THREE.Vector3(L - centerOffset.x, (L - W + L) / 2 - centerOffset.y, 0), 'right');
              addBtn(4, new THREE.Vector3(L / 2 - centerOffset.x, L - centerOffset.y, 0), 'top');
              addBtn(5, new THREE.Vector3(0 - centerOffset.x, L / 2 - centerOffset.y, 0), 'left');
              break;
            }
            case ShapeTypeEnum.Circle: {
              const radiusC = (plan.diameter || 940) / 2;
              // Circle center is (radius, radius) in 2D. Center to geometry center.
              // Center point is top (y = radius * 2). Local is radius*2 - centerOffset.y = radius.
              addBtn(0, new THREE.Vector3(0, radiusC, 0), 'circle');
              break;
            }
          }
        }
        
        if (interactionMode !== InteractionModeEnum.EDGE) {
          // Shared Helpers for Jamb/Cutaway
          // Squared distance from point p to segment v-w
          const distToSegmentSquared = (p: { x: number, y: number }, v: { x: number, y: number }, w: { x: number, y: number }) => {
            const l2 = (v.x - w.x) ** 2 + (v.y - w.y) ** 2;
            if (l2 === 0) return (p.x - v.x) ** 2 + (p.y - v.y) ** 2;
            let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
            t = Math.max(0, Math.min(1, t));
            const px = v.x + t * (w.x - v.x);
            const py = v.y + t * (w.y - v.y);
            return (p.x - px) ** 2 + (p.y - py) ** 2;
          };

          // Helper to check if ANY of the given world points are blocked by other plans
          const checkBlockedPoints = (worldPoints: THREE.Vector3[]) => {
            return activePlans.some(other => {
              if (other === obj) return false;

              const polygons = this.collisionService.getCollisionPolygons(other);
              if (!polygons || polygons.length === 0) return false;

              const THRESHOLD_SQ = 5 * 5; // 5mm tolerance squared

              return worldPoints.some(pt => {
                return polygons.some(poly => {
                  return poly.some((v, i) => {
                    const w = poly[(i + 1) % poly.length];
                    return distToSegmentSquared({ x: pt.x, y: pt.y }, v, w) < THRESHOLD_SQ;
                  });
                });
              });
            });
          };

          if (interactionMode === InteractionModeEnum.JAMB || interactionMode === InteractionModeEnum.SNAPSHOT) {
            const allowedTypes = [ShapeTypeEnum.Rectangle, ShapeTypeEnum.Mushroom, ShapeTypeEnum.HalfMoon];
            if (!allowedTypes.includes(plan.shape.shapeType)) return;

            const L = plan.length;
            const W = plan.width;
            const centerOffset = obj.userData['centerOffset'] || { x: L / 2, y: W / 2 };
            const localLeft = new THREE.Vector3(0 - centerOffset.x, W / 2 - centerOffset.y, 0);
            const localRight = new THREE.Vector3(L - centerOffset.x, W / 2 - centerOffset.y, 0);

            const checkBlockedSide = (side: 'left' | 'right', localPos: THREE.Vector3) => {
              const halfWidth = plan.width / 2;
              const topCornerLocal = localPos.clone().setY(halfWidth - centerOffset.y);
              const bottomCornerLocal = localPos.clone().setY(-centerOffset.y);

              const pointsToCheck = [toWorld(topCornerLocal), toWorld(bottomCornerLocal)];
              return checkBlockedPoints(pointsToCheck);
            };

            const isStraightSide = (side: 'left' | 'right') => {
              if (plan.shape.shapeType === ShapeTypeEnum.Rectangle) return true;
              if (plan.shape.shapeType === ShapeTypeEnum.Mushroom) return side === 'left';
              if (plan.shape.shapeType === ShapeTypeEnum.HalfMoon) return side === 'left';
              return false;
            };

            const addBtn = (idSuffix: number, localPoint: THREE.Vector3, side: 'left' | 'right') => {
              if (!isStraightSide(side)) return;
              if (checkBlockedSide(side, localPoint)) return;

              // Rule: No jamb if either corner of this side has a cutaway
              if (plan.configurationCutaways && plan.configurationCutaways.length > 0) {
                const len = plan.length;
                const wid = plan.width;
                const isSame = (p1: any, p2: any) => p1 && p2 && Math.abs(p1.x - p2.x) < 5 && Math.abs(p1.y - p2.y) < 5;
                // Corners of each side (in uncentered coordinates)
                let corners: { x: number, y: number }[] = [];
                if (side === 'left') {
                  corners = [{ x: 0, y: 0 }, { x: 0, y: wid }];
                } else if (side === 'right') {
                  corners = [{ x: len, y: 0 }, { x: len, y: wid }];
                }
                const hasConflictingCutaway = plan.configurationCutaways.some((c: any) =>
                  corners.some(corner => isSame(c.origin, corner))
                );
                if (hasConflictingCutaway) return;
              }

              const screen = toScreenPosition(toWorld(localPoint));
              const isActive = plan.configurationJambs?.some((j: any) => {
                if (j.side === side) return true;
                const len = plan.length;
                const wid = plan.width;
                let segStart, segEnd;
                if (side === 'left') { segStart = { x: 0, y: wid }; segEnd = { x: 0, y: 0 }; }
                else if (side === 'right') { segStart = { x: len, y: 0 }; segEnd = { x: len, y: wid }; }
                if (!segStart || !segEnd) return false;
                const isSame = (p1: any, p2: any) => p1 && p2 && Math.abs(p1.x - p2.x) < 2 && Math.abs(p1.y - p2.y) < 2;
                return (isSame(j.startPoint, segStart) && isSame(j.endPoint, segEnd)) ||
                  (isSame(j.startPoint, segEnd) && isSame(j.endPoint, segStart));
              });

              actionButtons.push({
                id: `jamb_${idSuffix}_${plan.displayOrder}`,
                type: 'finish_click',
                x: screen.x, y: screen.y,
                icon: 'arrow-down',
                tooltip: 'Pied',
                targetId: plan.displayOrder,
                isActive: isActive,
                data: { mode: 'jamb', side, localPoint: localPoint }
              });
            };

            addBtn(3, localLeft, 'left');
            addBtn(4, localRight, 'right');
          }
          
          if (interactionMode === InteractionModeEnum.CUTAWAY || interactionMode === InteractionModeEnum.SNAPSHOT) {
            const halfL = plan.length / 2;
            const halfW = plan.width / 2;

            // Define corners relative to local center
            // Each corner is tagged with the two sides it belongs to
            // For Angle shape: bounding box is (0,0)-(len,len) so Y uses halfL, not halfW.
            // Also the diagonal corner (pan coupé) is excluded from cutaway options.
            // The `origin` field stores the value as saved by toggleCutawaySelection
            // and is used for direct matching instead of the standard offset formula.
            type CornerDef = { pos: THREE.Vector3, sides: string[], origin?: { x: number, y: number } };
            let cornerDefs: CornerDef[];

            if (plan.shape.shapeType === ShapeTypeEnum.Angle) {
              cornerDefs = [
                { pos: new THREE.Vector3(-halfL, halfL, 0), sides: ['left', 'top'], origin: { x: 0, y: plan.length } },           // TL
                { pos: new THREE.Vector3(halfL, halfL, 0), sides: ['right', 'top'], origin: { x: plan.length, y: plan.length } }, // TR
                { pos: new THREE.Vector3(-halfL, -halfL, 0), sides: ['left', 'bottom'], origin: { x: 0, y: 0 } }                  // BL
                // Diagonal corner (pan coupé) is intentionally excluded
              ];
            } else {
              cornerDefs = [
                { pos: new THREE.Vector3(-halfL, halfW, 0), sides: ['left', 'top'] },    // TL
                { pos: new THREE.Vector3(halfL, halfW, 0), sides: ['right', 'top'] },   // TR
                { pos: new THREE.Vector3(-halfL, -halfW, 0), sides: ['left', 'bottom'] }, // BL
                { pos: new THREE.Vector3(halfL, -halfW, 0), sides: ['right', 'bottom'] } // BR
              ];
            }

            // Rule 1: No cutaways on rounded sides (HalfMoon, Mushroom)
            const roundedSides: string[] = [];
            if (plan.shape.shapeType === ShapeTypeEnum.HalfMoon || plan.shape.shapeType === ShapeTypeEnum.Mushroom) {
              roundedSides.push('right'); // The arc and the circle are on the right
            }

            // Rule 2: No cutaway on corners belonging to a side that has a jamb
            const sidesWithJamb: string[] = [];
            if (plan.configurationJambs && plan.configurationJambs.length > 0) {
              plan.configurationJambs.forEach((j: any) => {
                if (j.side) sidesWithJamb.push(j.side);
                // Also detect by geometry
                const len = plan.length;
                const wid = plan.width;
                const isSame = (p1: any, p2: any) => p1 && p2 && Math.abs(p1.x - p2.x) < 2 && Math.abs(p1.y - p2.y) < 2;
                if (isSame(j.startPoint, { x: 0, y: wid }) && isSame(j.endPoint, { x: 0, y: 0 }) ||
                  isSame(j.startPoint, { x: 0, y: 0 }) && isSame(j.endPoint, { x: 0, y: wid })) {
                  if (!sidesWithJamb.includes('left')) sidesWithJamb.push('left');
                }
                if (isSame(j.startPoint, { x: len, y: 0 }) && isSame(j.endPoint, { x: len, y: wid }) ||
                  isSame(j.startPoint, { x: len, y: wid }) && isSame(j.endPoint, { x: len, y: 0 })) {
                  if (!sidesWithJamb.includes('right')) sidesWithJamb.push('right');
                }
              });
            }

            let cid = 1;
            cornerDefs.forEach(corner => {
              // Skip if corner belongs to a rounded side
              if (corner.sides.some(s => roundedSides.includes(s))) return;
              // Skip if corner belongs to a side with a jamb
              if (corner.sides.some(s => sidesWithJamb.includes(s))) return;
              // Skip if blocked by another plan
              if (checkBlockedPoints([toWorld(corner.pos)])) return;

              const screen = toScreenPosition(toWorld(corner.pos));

              const isSame = (p1: any, p2: any) => p1 && p2 && Math.abs(p1.x - p2.x) < 2 && Math.abs(p1.y - p2.y) < 2;
              // Cutaway origins are stored in non-centered local coords (0..length, 0..width)
              // For Angle shape: corners use an explicit origin for direct matching
              // For other shapes: convert origin to centered coords using halfL/halfW offset
              const cut = plan.configurationCutaways?.find((c: any) => {
                if (corner.origin) {
                  return isSame(c.origin, corner.origin);
                }
                const centeredOrigin = { x: c.origin.x - halfL, y: c.origin.y - halfW };
                return isSame(centeredOrigin, corner.pos);
              });

              actionButtons.push({
                id: `cutaway_${cid++}_${plan.displayOrder}`,
                type: 'finish_click',
                x: screen.x,
                y: screen.y,
                icon: cut ? (cut.angleType === AngleTypeEnum.straight ? 'cut-straight' : 'cut-rounded') : 'cut',
                tooltip: 'Découpe',
                targetId: plan.displayOrder,
                isActive: !!cut,
                data: { mode: 'cutaway', plan, localPoint: corner.pos }
              });
            });

          }
          
          if (interactionMode === InteractionModeEnum.SPLASHBACK) {
            const L = plan.length;
            const W = plan.width;
            const centerOffset = obj.userData['centerOffset'] || (
              plan.shape.shapeType === ShapeTypeEnum.Angle
                ? { x: L / 2, y: L / 2 }
                : { x: L / 2, y: W / 2 }
            );

            const addBtn = (idSuffix: number, localPoint: THREE.Vector3, side: WorkbenchSide) => {
              // Ne pas proposer de crédence sur un côté impliqué dans un assemblage
              if (assembledSides.get(plan.displayOrder)?.has(side)) return;

              const screen = toScreenPosition(toWorld(localPoint));
              const sideSegment = getWorkbenchSideSegment(plan, side);
              const TOL = 2;
              const samePoint = (a: any, b: any) => a && b && Math.abs(a.x - b.x) < TOL && Math.abs(a.y - b.y) < TOL;
              const isActive = !!sideSegment && !!plan.configurationSplashbacks?.some((s: any) =>
                inferWorkbenchSideFromSegment(plan, s.startPoint, s.endPoint, TOL) === side ||
                (
                  samePoint(s.startPoint, sideSegment.start) &&
                  samePoint(s.endPoint, sideSegment.end)
                ) ||
                (
                  samePoint(s.startPoint, sideSegment.end) &&
                  samePoint(s.endPoint, sideSegment.start)
                )
              );
              actionButtons.push({
                id: `splashback_${idSuffix}_${plan.displayOrder}`,
                type: 'finish_click',
                x: screen.x, y: screen.y,
                icon: 'wall',
                tooltip: 'Crédence',
                targetId: plan.displayOrder,
                isActive: isActive,
                data: { mode: 'splashback', side, plan }
              });
            };

            switch (plan.shape.shapeType) {
              case ShapeTypeEnum.Rectangle: {
                addBtn(1, new THREE.Vector3(L / 2 - centerOffset.x, W - centerOffset.y, 0), 'top');
                addBtn(2, new THREE.Vector3(L / 2 - centerOffset.x, 0 - centerOffset.y, 0), 'bottom');
                addBtn(3, new THREE.Vector3(0 - centerOffset.x, W / 2 - centerOffset.y, 0), 'left');
                addBtn(4, new THREE.Vector3(L - centerOffset.x, W / 2 - centerOffset.y, 0), 'right');
                break;
              }
              case ShapeTypeEnum.HalfMoon: {
                const rHM = W / 2;
                addBtn(1, new THREE.Vector3((L - rHM) / 2 - centerOffset.x, W - centerOffset.y, 0), 'top');
                addBtn(2, new THREE.Vector3((L - rHM) / 2 - centerOffset.x, 0 - centerOffset.y, 0), 'bottom');
                addBtn(3, new THREE.Vector3(0 - centerOffset.x, W / 2 - centerOffset.y, 0), 'left');
                break;
              }
              case ShapeTypeEnum.Mushroom: {
                const rM = (plan.diameter || W) / 2;
                addBtn(1, new THREE.Vector3((L - rM) / 2 - centerOffset.x, W - centerOffset.y, 0), 'top');
                addBtn(2, new THREE.Vector3((L - rM) / 2 - centerOffset.x, 0 - centerOffset.y, 0), 'bottom');
                addBtn(3, new THREE.Vector3(0 - centerOffset.x, W / 2 - centerOffset.y, 0), 'left');
                break;
              }
              case ShapeTypeEnum.Angle: {
                addBtn(1, new THREE.Vector3(W / 2 - centerOffset.x, 0 - centerOffset.y, 0), 'bottom');
                addBtn(3, new THREE.Vector3(L - centerOffset.x, (L - W + L) / 2 - centerOffset.y, 0), 'right');
                addBtn(4, new THREE.Vector3(L / 2 - centerOffset.x, L - centerOffset.y, 0), 'top');
                addBtn(5, new THREE.Vector3(0 - centerOffset.x, L / 2 - centerOffset.y, 0), 'left');
                break;
              }
            }
          }
        }
      });
    }

    // Rotation Buttons (Step < 2) - Disable for Circle
    if (selectedObject && step < 2 && selectedObject.userData['plan']?.shape?.shapeType !== ShapeTypeEnum.Circle) {
      const cached = getCachedData(selectedObject);
      if (cached) {
        const bounds = cached.bounds;
        const centerPos = toScreenPosition(new THREE.Vector3(
          (bounds.left + bounds.right) / 2,
          (bounds.top + bounds.bottom) / 2,
          0
        ));

        actionButtons.push({
          id: 1, type: 'rotate_left', x: centerPos.x - 50, y: centerPos.y,
          icon: 'rotate-left', tooltip: 'Pivoter à gauche', targetId: selectedObject.userData['id']
        });
        actionButtons.push({
          id: 2, type: 'rotate_right', x: centerPos.x + 50, y: centerPos.y,
          icon: 'rotate-right', tooltip: 'Pivoter à droite', targetId: selectedObject.userData['id']
        });
      }
    }

    // ----------------------
    // Cutting Rotation Buttons (CUTTING mode only)
    // ----------------------
    if (interactionMode === InteractionModeEnum.CUTTING && selectedCuttingData) {
      activePlans.forEach(obj => {
        if (obj.userData['id'] !== selectedCuttingData!.planGroupId) return;
        const hitArea = obj.children.find(
          c => c.userData['isCuttingHitArea'] && c.userData['cuttingIndex'] === selectedCuttingData!.cuttingIdx
        );
        if (!hitArea) return;
        const worldPos = new THREE.Vector3();
        hitArea.getWorldPosition(worldPos);
        const screenPos = toScreenPosition(worldPos);
        actionButtons.push({
          id: 'cutting_rotate_left', type: 'rotate_cutting_left',
          x: screenPos.x - 40, y: screenPos.y,
          icon: 'rotate-left', tooltip: 'Pivoter à gauche',
          targetId: selectedCuttingData!.planGroupId,
          data: { cuttingIdx: selectedCuttingData!.cuttingIdx }
        });
        actionButtons.push({
          id: 'cutting_rotate_right', type: 'rotate_cutting_right',
          x: screenPos.x + 40, y: screenPos.y,
          icon: 'rotate-right', tooltip: 'Pivoter à droite',
          targetId: selectedCuttingData!.planGroupId,
          data: { cuttingIdx: selectedCuttingData!.cuttingIdx }
        });
      });
    }

    // ----------------------
    // Splashback Dimensions & Indicators (2D Only)
    // ----------------------
    if (!is3DMode && step >= 3) {
      activePlans.forEach(obj => {
        const plan = obj.userData['plan'];
        if (!plan) return;

        obj.children.forEach(child => {
          if (child.userData['type'] !== 'splashback') return;

          const height = child.userData['height'] as number;
          if (!height) return;

          const start = child.userData['start'] as THREE.Vector3;
          const end = child.userData['end'] as THREE.Vector3;
          const sbDisplayOrder = child.userData['displayOrder'] as number;

          // World position of splashback center
          const worldCenter = new THREE.Vector3();
          child.getWorldPosition(worldCenter);
          const sCenter = toScreenPosition(worldCenter);

          // Center label "X.Y"
          splashbackIndicators.push({
            x: sCenter.x,
            y: sCenter.y,
            label: sbDisplayOrder != null ? `${plan.displayOrder}.${sbDisplayOrder}` : `${plan.displayOrder}`
          });

          // Height dimension on the side of the splashback
          if (start && end) {
            // Transform start/end from plan local (centered) space to world
            const toPlanWorld = (v: THREE.Vector3) => v.clone().applyEuler(obj.rotation).add(obj.position);
            const worldStart = toPlanWorld(start);
            const worldEnd = toPlanWorld(end);

            const edgeVec = worldEnd.clone().sub(worldStart);
            const edgeLen = edgeVec.length();
            const edgeDirUnit = edgeVec.clone().normalize();
            // Perpendicular to edge = height axis of the splashback in world space
            const perpDir = new THREE.Vector3(-edgeDirUnit.y, edgeDirUnit.x, 0);

            // Place dimension just beyond the end of the splashback (worldEnd side)
            const SIDE_GAP = 40; // mm gap beyond the splashback end
            const dimCenter = worldCenter.clone().add(edgeDirUnit.clone().multiplyScalar(edgeLen / 2 + SIDE_GAP));

            // Endpoints of the dimension line (top/bottom of height at that side)
            const p1World = dimCenter.clone().add(perpDir.clone().multiplyScalar(height / 2));
            const p2World = dimCenter.clone().add(perpDir.clone().multiplyScalar(-height / 2));

            const p1Screen = toScreenPosition(p1World);
            const p2Screen = toScreenPosition(p2World);
            const dimCenterScreen = toScreenPosition(dimCenter);

            const angle = getProjectedAngle(p1World, p2World);
            const lineLen = Math.hypot(p2Screen.x - p1Screen.x, p2Screen.y - p1Screen.y);

            dimensions.push({
              id: `sb_dim_${child.parent?.userData['id'] ?? child.uuid}_${child.uuid}`,
              value: `${height}`,
              x: dimCenterScreen.x,
              y: dimCenterScreen.y,
              angle: angle,
              length: lineLen,
              isReadOnly: true
            });
          }
        });
      });
    }

    // ----------------------
    // Main Dimensions
    // ----------------------
    const OFFSET = 150;
    activePlans.forEach(obj => {
      const plan = obj.userData['plan'];
      if (!planDimensionSides[plan.displayOrder]) planDimensionSides[plan.displayOrder] = {};
      const cached = getCachedData(obj);
      if (!cached) return;
      const bounds = cached.bounds;

      let rotationDeg = Math.round(obj.rotation.z * (180 / Math.PI));
      // Normalize to 0-360
      rotationDeg = rotationDeg % 360;
      if (rotationDeg < 0) rotationDeg += 360;

      const isRotated90 = Math.abs(rotationDeg - 90) < 1 || Math.abs(rotationDeg - 270) < 1;

      // Helper Functions
      const toWorld = (v: THREE.Vector3) => v.clone().applyEuler(obj.rotation).add(obj.position);

      const distToSegmentSquared = (p: { x: number, y: number }, v: { x: number, y: number }, w: { x: number, y: number }) => {
        const l2 = (v.x - w.x) ** 2 + (v.y - w.y) ** 2;
        if (l2 === 0) return (p.x - v.x) ** 2 + (p.y - v.y) ** 2;
        let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
        t = Math.max(0, Math.min(1, t));
        const px = v.x + t * (w.x - v.x);
        const py = v.y + t * (w.y - v.y);
        return (p.x - px) ** 2 + (p.y - py) ** 2;
      };

      const checkBlockedPoints = (worldPoints: THREE.Vector3[]) => {
        return activePlans.some(other => {
          if (other === obj) return false;
          const polygons = this.collisionService.getCollisionPolygons(other);
          if (!polygons || polygons.length === 0) return false;
          const THRESHOLD_SQ = 5 * 5;
          return worldPoints.some(pt => {
            return polygons.some(poly => {
              return poly.some((v, i) => {
                const w = poly[(i + 1) % poly.length];
                return distToSegmentSquared({ x: pt.x, y: pt.y }, v, w) < THRESHOLD_SQ;
              });
            });
          });
        });
      };

      const halfL = plan.length / 2;
      const halfW = plan.width / 2;

      const isEdgeBlocked = (edge: 'left' | 'right' | 'top' | 'bottom'): boolean => {
        const pts = this.getEdgeProbePoints(plan, obj, edge);
        return pts.map(p => toWorld(p)).some(pt => checkBlockedPoints([pt]));
      };

      const groupId = obj.userData['groupId'];
      const groupDims = groupId ? groupDimensionSides[groupId] : null;

      let forceTop = false, forceBottom = false, forceLeft = false, forceRight = false;
      if (groupDims) {
        if (groupDims.top) forceTop = true;
        if (groupDims.bottom) forceBottom = true;
        if (groupDims.left) forceLeft = true;
        if (groupDims.right) forceRight = true;
      }

      let localForceTop = forceTop, localForceBottom = forceBottom, localForceLeft = forceLeft, localForceRight = forceRight;
      if (Math.abs(rotationDeg - 90) < 1) {
        localForceTop = forceLeft;
        localForceBottom = forceRight;
        localForceLeft = forceBottom;
        localForceRight = forceTop;
      } else if (Math.abs(rotationDeg - 180) < 1) {
        localForceTop = forceBottom;
        localForceBottom = forceTop;
        localForceLeft = forceRight;
        localForceRight = forceLeft;
      } else if (Math.abs(rotationDeg - 270) < 1) {
        localForceTop = forceRight;
        localForceBottom = forceLeft;
        localForceLeft = forceTop;
        localForceRight = forceBottom;
      }

      // Pre-calculate blocked sides for Dimensions
      const isTopBlocked = isEdgeBlocked('top');
      const isBottomBlocked = isEdgeBlocked('bottom');
      const isLeftBlocked = isEdgeBlocked('left');
      const isRightBlocked = isEdgeBlocked('right');

      const mapLocalToWorldEdge = (localEdge: 'top' | 'bottom' | 'left' | 'right'): 'top' | 'bottom' | 'left' | 'right' => {
        if (Math.abs(rotationDeg - 0) < 1) return localEdge;
        if (Math.abs(rotationDeg - 90) < 1) {
          if (localEdge === 'top') return 'left';
          if (localEdge === 'bottom') return 'right';
          if (localEdge === 'left') return 'bottom';
          if (localEdge === 'right') return 'top';
        }
        if (Math.abs(rotationDeg - 180) < 1) {
          if (localEdge === 'top') return 'bottom';
          if (localEdge === 'bottom') return 'top';
          if (localEdge === 'left') return 'right';
          if (localEdge === 'right') return 'left';
        }
        if (Math.abs(rotationDeg - 270) < 1) {
          if (localEdge === 'top') return 'right';
          if (localEdge === 'bottom') return 'left';
          if (localEdge === 'left') return 'top';
          if (localEdge === 'right') return 'bottom';
        }
        return localEdge;
      };

      // --- LENGTH DIMENSION (Measure X-axis, Place at Top or Bottom) ---
      // Value: plan.length
      let useTop = !isTopBlocked;

      if (plan.shape.shapeType !== ShapeTypeEnum.Circle && plan.shape.shapeType !== ShapeTypeEnum.Angle) {
        // A2: If both sides are blocked, do not display this dimension
        if (isTopBlocked && isBottomBlocked) {
          // Skip length dimension entirely
        } else {
          if (isTopBlocked && !isBottomBlocked) useTop = false;
          else if (!isTopBlocked && isBottomBlocked) useTop = true;
          else if (localForceTop) useTop = true;
          else if (localForceBottom) useTop = false;
          else useTop = true; // Default

          // Calculate Endpoints
          // Length Dim touches Left/Right boundaries.
          // If at Top: Line from (TL + 0,Offset) to (TR + 0,Offset)
          const localY = useTop ? halfW + OFFSET : -halfW - OFFSET;
          const p1Local = new THREE.Vector3(-halfL, localY, 0);
          const p2Local = new THREE.Vector3(halfL, localY, 0);

          const p1World = toWorld(p1Local);
          const p2World = toWorld(p2Local);
          const p1Screen = toScreenPosition(p1World);
          const p2Screen = toScreenPosition(p2World);
          const centerScreen = toScreenPosition(toWorld(new THREE.Vector3(0, localY, 0)));

          const angle = getProjectedAngle(p1World, p2World); // Use actual dimension endpoints for correct perspective angle

          dimensions.push({
            id: `dim_${plan.displayOrder}_length`,
            value: `${plan.length}`,
            x: centerScreen.x,
            y: centerScreen.y,
            angle: angle, // Use X-axis angle
            isCalculated: false,
            length: Math.hypot(p2Screen.x - p1Screen.x, p2Screen.y - p1Screen.y),
            isReversed: !useTop, // Reversed if bottom? 
            planId: step == 1 ? plan.displayOrder : undefined,
            dimensionType: 'length'
          });
          if (planDimensionSides[plan.displayOrder]) {
            (planDimensionSides[plan.displayOrder] as any).horizontal = useTop ? 'top' : 'bottom';
          }
        }
      }

      // --- WIDTH DIMENSION (Measure Y-axis, Place at Left or Right) ---
      // Value: plan.width
      let useLeft = !isLeftBlocked;

      if (plan.shape.shapeType !== ShapeTypeEnum.Circle && plan.shape.shapeType !== ShapeTypeEnum.Angle) {
        // A2: If both sides are blocked, do not display this dimension
        if (isLeftBlocked && isRightBlocked) {
          // Skip width dimension entirely
        } else {
          if (isLeftBlocked && !isRightBlocked) useLeft = false;
          else if (!isLeftBlocked && isRightBlocked) useLeft = true;
          else if (localForceLeft) useLeft = true;
          else if (localForceRight) useLeft = false;
          else useLeft = true; // A1: Default Left (largeur à gauche par défaut)

          const localX = useLeft ? -halfL - OFFSET : halfL + OFFSET;
          const p1Local = new THREE.Vector3(localX, halfW, 0); // Top
          const p2Local = new THREE.Vector3(localX, -halfW, 0); // Bottom

          const p1World = toWorld(p1Local);
          const p2World = toWorld(p2Local);
          const p1Screen = toScreenPosition(p1World);
          const p2Screen = toScreenPosition(p2World);
          const centerScreen = toScreenPosition(toWorld(new THREE.Vector3(localX, 0, 0)));

          const angle = getProjectedAngle(p1World, p2World); // Use actual dimension endpoints for correct perspective angle

          dimensions.push({
            id: `dim_${plan.displayOrder}_width`,
            value: `${plan.width}`,
            x: centerScreen.x,
            y: centerScreen.y,
            angle: angle,
            isCalculated: false,
            length: Math.hypot(p2Screen.x - p1Screen.x, p2Screen.y - p1Screen.y),
            isReversed: useLeft,
            planId: step == 1 ? plan.displayOrder : undefined,
            dimensionType: 'width'
          });
          if (planDimensionSides[plan.displayOrder]) {
            planDimensionSides[plan.displayOrder].vertical = useLeft ? 'left' : 'right';
          }
        }

        // -----------------------------------------------------------------------
        // MUSHROOM SPECIFIC ADDITIONS (Unified Geometric Alignment)
        // -----------------------------------------------------------------------
        if (plan.shape.shapeType === ShapeTypeEnum.Mushroom) {
          // Target: Only one dimension representing the "Stem Width" (plan.width)
          // We want this dimension to ALWAYS be on the "Base" (Flat) side of the mushroom.

          const dimIndex = dimensions.findIndex(d => d.id === `dim_${plan.displayOrder}_width`);
          if (dimIndex !== -1) {
            const dim = dimensions[dimIndex];
            const centerOffset = obj.userData['centerOffset'] || { x: plan.length / 2, y: plan.width / 2 };

            // Local Geometry of Base (Flat End)
            // Base is at x = 0. Spans y = 0 to y = plan.width.
            let localBaseCenter = { x: 0, y: plan.width / 2 };
            let localBaseNormal = { x: -1, y: 0 }; // Pointing Outwards (Left)

            // CHECK IF BASE SIDE IS BLOCKED -> FLIP TO CAP SIDE
            let useCapSide = false;
            let rot = Math.round(obj.rotation.z * (180 / Math.PI)) % 360;
            if (rot < 0) rot += 360;

            if (rot < 45 || rot >= 315) { // 0 deg (Base Left)
              if (isEdgeBlocked('left')) useCapSide = true;
            } else if (rot >= 45 && rot < 135) { // 90 deg (Base Bottom)
              if (isEdgeBlocked('bottom')) useCapSide = true;
            } else if (rot >= 135 && rot < 225) { // 180 deg (Base Right)
              if (isEdgeBlocked('right')) useCapSide = true;
            } else { // 270 deg (Base Top)
              if (isEdgeBlocked('top')) useCapSide = true;
            }

            if (useCapSide) {
              // Flip to Cap (Length, Width/2) pointing (1, 0)
              localBaseCenter = { x: plan.length, y: plan.width / 2 };
              localBaseNormal = { x: 1, y: 0 };
            }

            // Transform to World
            // 1. Position
            const worldBaseCenter = (new THREE.Vector3(localBaseCenter.x - centerOffset.x, localBaseCenter.y - centerOffset.y, 0)).applyEuler(obj.rotation).add(obj.position);
            const worldBaseNormal = new THREE.Vector3(localBaseNormal.x, localBaseNormal.y, 0).applyEuler(obj.rotation).normalize();

            // Dimension Center Position
            const dimCenterWorld = worldBaseCenter.clone().add(worldBaseNormal.clone().multiplyScalar(OFFSET));
            const dimScreenPos = toScreenPosition(dimCenterWorld);

            dim.x = dimScreenPos.x;
            dim.y = dimScreenPos.y;

            // 2. Length (Points)
            const p1World = (new THREE.Vector3(0 - centerOffset.x, 0 - centerOffset.y, 0)).applyEuler(obj.rotation).add(obj.position);
            const p2World = (new THREE.Vector3(0 - centerOffset.x, plan.width - centerOffset.y, 0)).applyEuler(obj.rotation).add(obj.position);

            const sP1 = toScreenPosition(p1World);
            const sP2 = toScreenPosition(p2World);

            dim.length = Math.hypot(sP2.x - sP1.x, sP2.y - sP1.y);

            // 3. Orientation (Reversed?)
            const nx = worldBaseNormal.x;
            const ny = worldBaseNormal.y;

            // Robust check
            if (ny < -0.5) dim.isReversed = true; // Bottom
            else if (ny > 0.5) dim.isReversed = false; // Top
            else if (nx < -0.5) dim.isReversed = true; // Left
            else dim.isReversed = false; // Right

            dim.value = `${plan.width}`;
          }
        }
      } // End if (!Circle && !Angle)

      // 2. Add Diameter Indicator (Mushrooms Only)
      if (plan.shape.shapeType === ShapeTypeEnum.Mushroom) {
        const diam = plan.diameter && plan.diameter > 0 ? plan.diameter : (isRotated90 ? plan.length : plan.width);
        const r = diam / 2;

        // Calculate Bulb Center in Local Space then Transform
        const localCX = plan.length - r;
        const localCY = plan.isFlip ? (plan.width - r) : r;

        const centerOffset = obj.userData['centerOffset'] || { x: plan.length / 2, y: plan.width / 2 };

        const bulbCenterWorld = (new THREE.Vector3(localCX - centerOffset.x, localCY - centerOffset.y, 0)).applyEuler(obj.rotation).add(obj.position);

        // Radius for indicator line
        const margin = 50;
        const r_inner = Math.max(0, r - margin);

        // Visual Line Points (World Space)
        const localTop = new THREE.Vector3(localCX, localCY + r_inner, 0);
        const localBottom = new THREE.Vector3(localCX, localCY - r_inner, 0);

        // Transform to World
        const p1 = (new THREE.Vector3(localTop.x - centerOffset.x, localTop.y - centerOffset.y, 0)).applyEuler(obj.rotation).add(obj.position);
        const p2 = (new THREE.Vector3(localBottom.x - centerOffset.x, localBottom.y - centerOffset.y, 0)).applyEuler(obj.rotation).add(obj.position);

        const diamPos = toScreenPosition(bulbCenterWorld);
        const diamAngle = getProjectedAngle(p1, p2);
        const sP1 = toScreenPosition(p1);
        const sP2 = toScreenPosition(p2);
        const diamLength = Math.hypot(sP2.x - sP1.x, sP2.y - sP1.y);

        dimensions.push({
          id: `dim_${plan.displayOrder}_diameter`,
          value: `${Math.round(diam)}`,
          prefix: 'Ø',
          x: diamPos.x,
          y: diamPos.y,
          angle: diamAngle,
          length: diamLength,
          planId: step == 1 ? plan.displayOrder : undefined,
          dimensionType: 'diameter',
          isCalculated: true
        });
      } // End of Mushroom specific logic

      // 3. Add Intermediate (Partial) Calculated Dimensions
      if (plan.shape.shapeType !== ShapeTypeEnum.Circle && plan.shape.shapeType !== ShapeTypeEnum.Angle) {

        const subtractIntervals = (segs: { min: number, max: number }[], ovMin: number, ovMax: number) => {
          const res: { min: number, max: number }[] = [];
          segs.forEach(seg => {
            if (ovMax <= seg.min || ovMin >= seg.max) {
              res.push(seg);
            } else {
              if (ovMin > seg.min) res.push({ min: seg.min, max: ovMin });
              if (ovMax < seg.max) res.push({ min: ovMax, max: seg.max });
            }
          });
          return res;
        };

        const addPartialDimensions = (edge: 'left' | 'right' | 'top' | 'bottom') => {
          let axis: 'x' | 'y';
          let fixedVal: number, minVal: number, maxVal: number;
          if (edge === 'left') { axis = 'y'; fixedVal = bounds.left; minVal = bounds.top; maxVal = bounds.bottom; }
          else if (edge === 'right') { axis = 'y'; fixedVal = bounds.right; minVal = bounds.top; maxVal = bounds.bottom; }
          else if (edge === 'bottom') { axis = 'x'; fixedVal = bounds.top; minVal = bounds.left; maxVal = bounds.right; }
          else { axis = 'x'; fixedVal = bounds.bottom; minVal = bounds.left; maxVal = bounds.right; }

          let segments = [{ min: minVal, max: maxVal }];

          activePlans.forEach(other => {
            if (other === obj) return;
            const polygons = this.collisionService.getCollisionPolygons(other);
            if (!polygons || polygons.length === 0) return;

            let overlaps = false;
            let overlapMin = Infinity, overlapMax = -Infinity;

            polygons.forEach((poly: Point2D[]) => {
              for (let i = 0; i < poly.length; i++) {
                const pA = poly[i];
                const pB = poly[(i + 1) % poly.length];

                if (axis === 'y') {
                  // Testing vertical edge at x = fixedVal.
                  // We check if segment pA-pB is also roughly vertical and close in X.
                  if (Math.abs(pA.x - pB.x) < 50 && Math.abs(pA.x - fixedVal) < 20) {
                    overlaps = true;
                    overlapMin = Math.min(overlapMin, Math.min(pA.y, pB.y));
                    overlapMax = Math.max(overlapMax, Math.max(pA.y, pB.y));
                  }
                } else {
                  // Testing horizontal edge at y = fixedVal.
                  // We check if segment pA-pB is roughly horizontal and close in Y.
                  if (Math.abs(pA.y - pB.y) < 50 && Math.abs(pA.y - fixedVal) < 20) {
                    overlaps = true;
                    overlapMin = Math.min(overlapMin, Math.min(pA.x, pB.x));
                    overlapMax = Math.max(overlapMax, Math.max(pA.x, pB.x));
                  }
                }
              }
            });

            if (overlaps && overlapMin !== Infinity) {
              segments = subtractIntervals(segments, overlapMin, overlapMax);
            }
          });

          // Check if truly partially blocked
          const isCalculated = segments.length > 1 || (segments.length === 1 && (segments[0].min > minVal + 5 || segments[0].max < maxVal - 5));

          if (isCalculated) {
            segments.filter(s => s.max - s.min > 50).forEach((seg, idx) => {
              const lenVal = Math.round(seg.max - seg.min);
              let wP1: THREE.Vector3, wP2: THREE.Vector3, wMid: THREE.Vector3;
              let isVertical = axis === 'y';

              if (isVertical) {
                const xW = edge === 'left' ? fixedVal - OFFSET : fixedVal + OFFSET;
                wP1 = new THREE.Vector3(fixedVal, seg.max, 0); // Top
                wP2 = new THREE.Vector3(fixedVal, seg.min, 0); // Bottom
                wMid = new THREE.Vector3(xW, (seg.min + seg.max) / 2, 0);
              } else {
                const yW = edge === 'bottom' ? fixedVal - OFFSET : fixedVal + OFFSET;
                wP1 = new THREE.Vector3(seg.min, fixedVal, 0); // Left
                wP2 = new THREE.Vector3(seg.max, fixedVal, 0); // Right
                wMid = new THREE.Vector3((seg.min + seg.max) / 2, yW, 0);
              }

              const s1 = toScreenPosition(wP1);
              const s2 = toScreenPosition(wP2);
              const sMid = toScreenPosition(wMid);
              const angle = getProjectedAngle(wP1, wP2);

              dimensions.push({
                id: `partial_${plan.displayOrder}_${edge}_${idx}`,
                value: `${lenVal}`,
                x: sMid.x,
                y: sMid.y,
                angle: angle,
                length: Math.hypot(s2.x - s1.x, s2.y - s1.y),
                dimensionType: isVertical ? 'width' : 'length',
                isCalculated: true,
                isReadOnly: true,
                isReversed: edge === 'bottom' || edge === 'left'
              });
            });
          }
        };

        if (!useTop) addPartialDimensions(mapLocalToWorldEdge('top'));
        if (useTop) addPartialDimensions(mapLocalToWorldEdge('bottom')); // Bottom doesn't have editable dim
        if (!useLeft) addPartialDimensions(mapLocalToWorldEdge('left'));
        if (useLeft) addPartialDimensions(mapLocalToWorldEdge('right')); // Right doesn't have editable dim
      }

      // Circle & Mushroom Dimensions (Copy similar logic)
      if (plan.shape.shapeType === ShapeTypeEnum.Circle && plan.diameter) {
        const diamPos = toScreenPosition(new THREE.Vector3(
          (bounds.left + bounds.right) / 2,
          (bounds.top + bounds.bottom) / 2 + 200, // Move 200mm UP (World Y) to be above Index
          0
        ));
        dimensions.push({
          id: `dim_${plan.displayOrder}_diameter`,
          value: `${plan.diameter}`,
          prefix: 'Ø',
          x: diamPos.x,
          y: diamPos.y, // Centered inside
          angle: 0,
          planId: step == 1 ? plan.displayOrder : undefined,
          dimensionType: 'diameter'
        });
      }

      // Angle Shape Dimensions (4 sides: Full L, Full L, Partial W, Partial W)
      if (plan.shape.shapeType === ShapeTypeEnum.Angle) {
        const L = plan.length;
        const W = plan.width; // 'Straight' size

        // Helper to transform local (0,0 at bottom-left implied by mesh builder?) 
        // MeshBuilder: P0=(0,0), P3=(L,L). Center is approx L/2, L/2.
        const centerOffset = obj.userData['centerOffset'] || { x: L / 2, y: L / 2 };

        const toWorld = (lx: number, ly: number) => {
          const mx = lx - centerOffset.x;
          const my = ly - centerOffset.y;
          const v = new THREE.Vector3(mx, my, 0);
          v.applyEuler(obj.rotation).add(obj.position);
          return v;
        };

        // 1. Top (Full L, P4->P3)
        // Local: (0, L) -> (L, L)
        if (!isEdgeBlocked('top')) {
          const pStart = toWorld(0, L);
          const pEnd = toWorld(L, L);
          const pMid = toWorld(L / 2, L + OFFSET); // Outward
          const sMid = toScreenPosition(pMid);
          const sStart = toScreenPosition(pStart);
          const sEnd = toScreenPosition(pEnd);
          const angle = getProjectedAngle(pStart, pEnd);
          const len = Math.hypot(sEnd.x - sStart.x, sEnd.y - sStart.y);

          dimensions.push({
            id: `dim_${plan.displayOrder}_top`,
            value: `${L}`,
            x: sMid.x,
            y: sMid.y,
            angle: angle,
            length: len,
            planId: step == 1 ? plan.displayOrder : undefined,
            dimensionType: 'length',
            isCalculated: false
          });
        }

        // 2. Left (Full L, P0->P4)
        // Local: (0, 0) -> (0, L)
        if (!isEdgeBlocked('left')) {
          const pStart = toWorld(0, 0);
          const pEnd = toWorld(0, L);
          const pMid = toWorld(-OFFSET, L / 2); // Outward
          const sMid = toScreenPosition(pMid);
          const sStart = toScreenPosition(pStart);
          const sEnd = toScreenPosition(pEnd);
          const angle = getProjectedAngle(pStart, pEnd);
          const len = Math.hypot(sEnd.x - sStart.x, sEnd.y - sStart.y);

          dimensions.push({
            id: `dim_${plan.displayOrder}_left`,
            value: `${L}`,
            x: sMid.x,
            y: sMid.y,
            angle: angle,
            length: len,
            planId: step == 1 ? plan.displayOrder : undefined,
            dimensionType: 'length',
            isCalculated: false
          });
        }

        // 3. Bottom (Partial W, P0->P1)
        // Local: (0, 0) -> (W, 0)
        if (!isEdgeBlocked('bottom')) {
          const pStart = toWorld(0, 0);
          const pEnd = toWorld(W, 0);
          const pMid = toWorld(W / 2, -OFFSET); // Outward
          const sMid = toScreenPosition(pMid);
          const sStart = toScreenPosition(pStart);
          const sEnd = toScreenPosition(pEnd);
          const angle = getProjectedAngle(pStart, pEnd);
          const len = Math.hypot(sEnd.x - sStart.x, sEnd.y - sStart.y);

          dimensions.push({
            id: `dim_${plan.displayOrder}_bottom`,
            value: `${W}`,
            x: sMid.x,
            y: sMid.y,
            angle: angle,
            length: len,
            planId: step == 1 ? plan.displayOrder : undefined,
            dimensionType: 'width',
            isCalculated: false
          });
        }

        // 4. Right (Partial W, P2->P3)
        // Local: (L, L-W) -> (L, L)
        if (!isEdgeBlocked('right')) {
          const pStart = toWorld(L, L - W);
          const pEnd = toWorld(L, L);
          const pMid = toWorld(L + OFFSET, L - W / 2); // Outward
          const sMid = toScreenPosition(pMid);
          const sStart = toScreenPosition(pStart);
          const sEnd = toScreenPosition(pEnd);
          const angle = getProjectedAngle(pStart, pEnd);
          const len = Math.hypot(sEnd.x - sStart.x, sEnd.y - sStart.y);

          dimensions.push({
            id: `dim_${plan.displayOrder}_right`,
            value: `${W}`,
            x: sMid.x,
            y: sMid.y,
            angle: angle,
            length: len,
            planId: step == 1 ? plan.displayOrder : undefined,
            dimensionType: 'length',
            isCalculated: false
          });
        }
      }
    });

    // ----------------------
    // Group Visuals
    // ----------------------
    if (step >= 1) {
      Object.keys(groups).forEach((gid, index) => {
        const members = groups[gid];
        if (members.length > 0) {
          // Calculate Group Bounds
          let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
          let maxPlanWidth = 0;
          let maxPlanLength = 0;

          // Collect all plan bounds to check alignment
          const planBounds: { id: number, minX: number, maxX: number, minY: number, maxY: number }[] = [];

          members.forEach(m => {
            const plan = m.userData['plan'];
            if (plan) {
              // VISUAL DIMENSION CHECK
              let visualWidth = plan.length;
              let visualHeight = plan.width;

              let rot = Math.round(m.rotation.z * (180 / Math.PI)) % 360;
              if (rot < 0) rot += 360;
              if (Math.abs(rot - 90) < 1 || Math.abs(rot - 270) < 1) {
                visualWidth = plan.width;
                visualHeight = plan.length;
              }

              if (visualWidth > maxPlanWidth) maxPlanWidth = visualWidth;
              if (visualHeight > maxPlanLength) maxPlanLength = visualHeight;

              // Calculate Bounds for this plan
              const polygons = getCachedData(m)?.polygons || this.collisionService.getCollisionPolygons(m);
              let pMinX = Infinity, pMaxX = -Infinity, pMinY = Infinity, pMaxY = -Infinity;

              let hasPoints = false;
              polygons.forEach(poly => {
                poly.forEach(p => {
                  hasPoints = true;
                  if (p.x < minX) minX = p.x;
                  if (p.x > maxX) maxX = p.x;
                  if (p.y < minY) minY = p.y;
                  if (p.y > maxY) maxY = p.y;

                  if (p.x < pMinX) pMinX = p.x;
                  if (p.x > pMaxX) pMaxX = p.x;
                  if (p.y < pMinY) pMinY = p.y;
                  if (p.y > pMaxY) pMaxY = p.y;
                });
              });

              if (hasPoints) {
                planBounds.push({ id: plan.id, minX: pMinX, maxX: pMaxX, minY: pMinY, maxY: pMaxY });
              }
            }
          });

          if ((step === 1 || step === 4) && Number.isFinite(minX)) {
            const margin = 350;
            const SNAP_TOLERANCE = 40;
            const REDUNDANCY_TOLERANCE = 10;

            const checkSides = [
              { name: 'left', base: minX, axis: 'y', check: (b: any) => Math.abs(b.minX - minX) < SNAP_TOLERANCE },
              { name: 'right', base: maxX, axis: 'y', check: (b: any) => Math.abs(b.maxX - maxX) < SNAP_TOLERANCE },
              { name: 'top', base: maxY, axis: 'x', check: (b: any) => Math.abs(b.maxY - maxY) < SNAP_TOLERANCE },
              { name: 'bottom', base: minY, axis: 'x', check: (b: any) => Math.abs(b.minY - minY) < SNAP_TOLERANCE }
            ];

            // Phase 1: Collect span info per side
            const sideSpans: { [key: string]: { spanMin: number, spanMax: number, spanLength: number, axis: string } } = {};

            checkSides.forEach(side => {
              const aligned = planBounds.filter(b => side.check(b));
              if (aligned.length === 0) return;

              let spanMin = Infinity;
              let spanMax = -Infinity;

              aligned.forEach(b => {
                if (side.axis === 'y') {
                  if (b.minY < spanMin) spanMin = b.minY;
                  if (b.maxY > spanMax) spanMax = b.maxY;
                } else {
                  if (b.minX < spanMin) spanMin = b.minX;
                  if (b.maxX > spanMax) spanMax = b.maxX;
                }
              });

              const spanLength = spanMax - spanMin;
              const maxSingle = side.axis === 'y' ? maxPlanLength : maxPlanWidth;

              if (spanLength > maxSingle + REDUNDANCY_TOLERANCE) {
                sideSpans[side.name] = { spanMin, spanMax, spanLength, axis: side.axis };
              }
            });

            // Phase 2: Deduplicate opposite sides with same span
            // Determine majority side from individual dimension placements
            let hCounts = { top: 0, bottom: 0 };
            let vCounts = { left: 0, right: 0 };
            members.forEach(m => {
              const plan = m.userData['plan'];
              if (plan) {
                const sides = planDimensionSides[plan.displayOrder];
                if (sides) {
                  if (sides.horizontal === 'top') hCounts.top++;
                  if (sides.horizontal === 'bottom') hCounts.bottom++;
                  if (sides.vertical === 'left') vCounts.left++;
                  if (sides.vertical === 'right') vCounts.right++;
                }
              }
            });

            // If top & bottom have same span, keep the side with more individual dims
            if (sideSpans['top'] && sideSpans['bottom'] && Math.abs(sideSpans['top'].spanLength - sideSpans['bottom'].spanLength) < REDUNDANCY_TOLERANCE) {
              if (hCounts.top >= hCounts.bottom) {
                delete sideSpans['bottom'];
              } else {
                delete sideSpans['top'];
              }
            }
            // If left & right have same span, keep the side with more individual dims
            if (sideSpans['left'] && sideSpans['right'] && Math.abs(sideSpans['left'].spanLength - sideSpans['right'].spanLength) < REDUNDANCY_TOLERANCE) {
              if (vCounts.left >= vCounts.right) {
                delete sideSpans['right'];
              } else {
                delete sideSpans['left'];
              }
            }

            // Phase 3: Emit dimensions for remaining sides
            Object.keys(sideSpans).forEach(sideName => {
              const info = sideSpans[sideName];
              const isVertical = info.axis === 'y';
              const dimValue = Math.round(info.spanLength).toString();
              let len = 0;

              if (isVertical) {
                const xBase = sideName === 'left' ? minX : maxX;
                const xWorld = sideName === 'left' ? minX - margin : maxX + margin;

                const wP1 = new THREE.Vector3(xBase, info.spanMin, 0);
                const wP2 = new THREE.Vector3(xBase, info.spanMax, 0);
                const wMid = new THREE.Vector3(xWorld, (info.spanMin + info.spanMax) / 2, 0);

                const s1 = toScreenPosition(wP1);
                const s2 = toScreenPosition(wP2);
                const sMid = toScreenPosition(wMid);
                const len = Math.hypot(s2.x - s1.x, s2.y - s1.y);

                dimensions.push({
                  id: `group_${index}_${sideName}`,
                  value: `${dimValue}`,
                  x: sMid.x,
                  y: sMid.y,
                  angle: -90, // Force vertical
                  length: len,
                  dimensionType: 'length',
                  isCalculated: true,
                  isReadOnly: true,
                  isReversed: sideName === 'left'
                });

              } else {
                const yBase = sideName === 'bottom' ? minY : maxY;
                const yWorld = sideName === 'bottom' ? minY - margin : maxY + margin;

                const wP1 = new THREE.Vector3(info.spanMin, yBase, 0);
                const wP2 = new THREE.Vector3(info.spanMax, yBase, 0);
                const wMid = new THREE.Vector3((info.spanMin + info.spanMax) / 2, yWorld, 0);

                const s1 = toScreenPosition(wP1);
                const s2 = toScreenPosition(wP2);
                const sMid = toScreenPosition(wMid);
                len = Math.hypot(s2.x - s1.x, s2.y - s1.y);

                dimensions.push({
                  id: `group_${index}_${sideName}`,
                  value: `${dimValue}`,
                  x: sMid.x,
                  y: sMid.y,
                  angle: getProjectedAngle(wP1, wP2),
                  length: len,
                  dimensionType: 'length',
                  isCalculated: true,
                  isReadOnly: true,
                  isReversed: sideName === 'bottom'
                });
              }
            });

            // Group Indicator (Center)
            const center = new THREE.Vector3();
            const box = new THREE.Box3();
            members.forEach(m => box.expandByObject(m));
            box.getCenter(center);
            const sCenter = toScreenPosition(center);
            groupIndicators.push({
              label: groupLabelMap[gid],
              x: sCenter.x,
              y: sCenter.y
            });
          }
        }
      });
    }

    // Indicators
    activePlans.forEach(child => {
      if (child.userData['plan']) {
        const cached = getCachedData(child);
        if (!cached) return;
        const bounds = cached.bounds;
        const centerPos = toScreenPosition(new THREE.Vector3(
          (bounds.left + bounds.right) / 2,
          (bounds.top + bounds.bottom) / 2,
          0
        ));
        let label = child.userData['id'].toString();
        const groupId = child.userData['groupId'];
        if (groupId && groupLabelMap[groupId]) {
          label = `${label} / ${groupLabelMap[groupId]}`;
        }
        planIndicators.push({ x: centerPos.x, y: centerPos.y, label });
      }
    });

    // ----------------------
    // Assembly Icons (Magnetism Visuals)
    // ----------------------
    const ASSEMBLY_THRESHOLD = 2;
    const roundedTypes = new Set<ShapeTypeEnum>([ShapeTypeEnum.HalfMoon, ShapeTypeEnum.Mushroom]);

    // A3: Helper to determine which world-space side is rounded for a given plan+rotation
    const getRoundedWorldSide = (planObj: THREE.Object3D): 'left' | 'right' | 'top' | 'bottom' | null => {
      const p = planObj.userData['plan'];
      if (!roundedTypes.has(p.shape.shapeType)) return null;
      // In local space, the rounded side is always "right" (+X / length side)
      let rot = Math.round(planObj.rotation.z * (180 / Math.PI)) % 360;
      if (rot < 0) rot += 360;
      if (rot >= 315 || rot < 45) return 'right';   // 0°   → local right = world right
      if (rot >= 45 && rot < 135) return 'top';      // 90°  → local right = world top
      if (rot >= 135 && rot < 225) return 'left';    // 180° → local right = world left
      return 'bottom';                                // 270° → local right = world bottom
    };

    for (let i = 0; i < activePlans.length; i++) {
      const obj = activePlans[i];
      const plan = obj.userData['plan'];
      if (plan.shape.shapeType === ShapeTypeEnum.Circle) continue;

      const objCached = getCachedData(obj);
      if (!objCached) continue;
      const objBounds = objCached.bounds;
      const objRoundedSide = getRoundedWorldSide(obj);

      for (let j = i + 1; j < activePlans.length; j++) {
        const other = activePlans[j];
        const otherPlan = other.userData['plan'];
        if (otherPlan.shape.shapeType === ShapeTypeEnum.Circle) continue;

        const otherCached = getCachedData(other);
        if (!otherCached) continue;
        const otherBounds = otherCached.bounds;
        const otherRoundedSide = getRoundedWorldSide(other);

        let iconPos: { x: number, y: number } | null = null;
        let worldPos: THREE.Vector3 | null = null;

        // Check Right-Left (obj's right touches other's left)
        if (objRoundedSide !== 'right' && Math.abs(objBounds.right - otherBounds.left) < ASSEMBLY_THRESHOLD) {
          const overlapY = Math.min(objBounds.bottom, otherBounds.bottom) - Math.max(objBounds.top, otherBounds.top);
          if (overlapY > 50) {
            worldPos = new THREE.Vector3(
              otherBounds.left,
              (Math.max(objBounds.top, otherBounds.top) + Math.min(objBounds.bottom, otherBounds.bottom)) / 2,
              0
            );
          }
        }
        // Check Left-Right (obj's left touches other's right)
        else if (otherRoundedSide !== 'right' && objRoundedSide !== 'left' && Math.abs(objBounds.left - otherBounds.right) < ASSEMBLY_THRESHOLD) {
          const overlapY = Math.min(objBounds.bottom, otherBounds.bottom) - Math.max(objBounds.top, otherBounds.top);
          if (overlapY > 50) {
            worldPos = new THREE.Vector3(
              otherBounds.right,
              (Math.max(objBounds.top, otherBounds.top) + Math.min(objBounds.bottom, otherBounds.bottom)) / 2,
              0
            );
          }
        }
        // Check Top-Bottom (obj's top touches other's bottom — i.e. obj's minY = Y-up top)
        else if (objRoundedSide !== 'top' && otherRoundedSide !== 'bottom' && Math.abs(objBounds.top - otherBounds.bottom) < ASSEMBLY_THRESHOLD) {
          const overlapX = Math.min(objBounds.right, otherBounds.right) - Math.max(objBounds.left, otherBounds.left);
          if (overlapX > 50) {
            worldPos = new THREE.Vector3(
              (Math.max(objBounds.left, otherBounds.left) + Math.min(objBounds.right, otherBounds.right)) / 2,
              otherBounds.bottom,
              0
            );
          }
        }
        // Check Bottom-Top
        else if (objRoundedSide !== 'bottom' && otherRoundedSide !== 'top' && Math.abs(objBounds.bottom - otherBounds.top) < ASSEMBLY_THRESHOLD) {
          const overlapX = Math.min(objBounds.right, otherBounds.right) - Math.max(objBounds.left, otherBounds.left);
          if (overlapX > 50) {
            worldPos = new THREE.Vector3(
              (Math.max(objBounds.left, otherBounds.left) + Math.min(objBounds.right, otherBounds.right)) / 2,
              otherBounds.top,
              0
            );
          }
        }

        if (worldPos) {
          iconPos = toScreenPosition(worldPos);
          magnetismIcons.push({ x: iconPos.x, y: iconPos.y });
        }
      }
    }

    return {
      actionButtons,
      dimensions,
      planIndicators,
      groupIndicators,
      magnetismIcons,
      splashbackIndicators
    };
  }
}
